import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { NotificationCenter } from "./NotificationCenter";
import logo from "@/assets/logo.png";

const Navigation = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userId, setUserId] = useState<string | undefined>();

  useEffect(() => {
    // Check if user is logged in
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log("Navigation - Session check:", session ? "Logged in" : "Logged out");
      setIsLoggedIn(!!session);
      setUserId(session?.user?.id);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log("Navigation - Auth state changed:", session ? "Logged in" : "Logged out");
      setIsLoggedIn(!!session);
      setUserId(session?.user?.id);
    });

    return () => subscription.unsubscribe();
  }, []);

  const navItems = [
    { label: "Home", path: "/" },
    { label: "Curriculum", path: "/curriculum" },
    { label: "Success Stories", path: "/case-studies" },
    { label: "Pricing", path: "/pricing" },
    { label: "Blog", path: "/blog" },
    { label: "About", path: "/about" },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-3 group"
          >
            <img 
              src={logo} 
              alt="Launch To Revenue Logo" 
              className="h-10 w-10 transition-transform group-hover:scale-110"
            />
            <span className="text-xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              Launch To Revenue
            </span>
          </button>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              >
                {item.label}
              </button>
            ))}
            {isLoggedIn && <NotificationCenter userId={userId} />}
            {/* Sign In Link */}
            <button
              onClick={() => navigate("/sign-in")}
              className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              aria-label="Sign in"
            >
              Sign In
            </button>
            <Button onClick={() => navigate(isLoggedIn ? "/dashboard" : "/sign-up")}>
              {isLoggedIn ? "Dashboard" : "Join Today"}
            </Button>
            {isLoggedIn && (
              <button
                onClick={async () => {
                  try {
                    await supabase.auth.signOut({ scope: 'global' });
                  } catch (error) {
                    console.log("Logout global failed, clearing local session:", error);
                    await supabase.auth.signOut({ scope: 'local' });
                  } finally {
                    // Always clear local state and redirect
                    setIsLoggedIn(false);
                    setUserId(undefined);
                    navigate("/");
                  }
                }}
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                aria-label="Sign out"
              >
                Sign out
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 space-y-4 border-t">
            {navItems.map((item) => (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setIsMenuOpen(false);
                }}
                className="block w-full text-left text-sm font-medium text-muted-foreground hover:text-primary transition-colors py-2"
              >
                {item.label}
              </button>
            ))}
            {/* Sign In Link */}
            <button
              onClick={() => {
                navigate("/sign-in");
                setIsMenuOpen(false);
              }}
              className="block w-full text-left text-sm font-medium text-muted-foreground hover:text-primary transition-colors py-2"
              aria-label="Sign in"
            >
              Sign In
            </button>
            <Button
              onClick={() => {
                navigate(isLoggedIn ? "/dashboard" : "/sign-up");
                setIsMenuOpen(false);
              }}
              className="w-full"
            >
              {isLoggedIn ? "Dashboard" : "Join Today"}
            </Button>
            {isLoggedIn && (
              <button
                onClick={async () => {
                  try {
                    await supabase.auth.signOut({ scope: 'global' });
                  } catch (error) {
                    console.log("Logout global failed, clearing local session:", error);
                    await supabase.auth.signOut({ scope: 'local' });
                  } finally {
                    // Always clear local state and redirect
                    setIsLoggedIn(false);
                    setIsMenuOpen(false);
                    navigate("/");
                  }
                }}
                className="block w-full text-left text-sm font-medium text-muted-foreground hover:text-primary transition-colors py-2"
                aria-label="Sign out"
              >
                Sign out
              </button>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
